/**
 * Guardrails Configuration Hooks
 *
 * React Query hooks for guardrails at two levels:
 * - Platform-level guardrails (system admin only)
 * - Tenant-level guardrails (admin)
 * - Effective guardrails for an agent (read-only preview)
 *
 * API:
 *   GET/PUT  /api/v1/admin/config/guardrails         → Platform
 *   GET/PUT  /api/v1/tenants/{tid}/config               → Tenant (guardrail_prompts field)
 *   GET      /api/v1/tenants/{tid}/config/agents/{aid}/effective-guardrails → Effective
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTenant } from '@/contexts/TenantContext';
import apiClient from '@/lib/api-client';

// =============================================================================
// Types
// =============================================================================

export interface PlatformGuardrails {
  guardrails: string[];
}

export interface EffectiveGuardrails {
  platform: string[];
  tenant: string[];
  agent: string[];
  merged: string[];
}

export interface DetectionStrategyConfig {
  action: 'off' | 'log_only' | 'block';
  strategy: 'patterns' | 'ai' | 'contextual_analysis';
}

export interface DataGovernanceConfig {
  pii: DetectionStrategyConfig;
  code_danger: DetectionStrategyConfig;
  credential: DetectionStrategyConfig;
  violence: DetectionStrategyConfig;
  hate: DetectionStrategyConfig;
}

// =============================================================================
// Query Keys
// =============================================================================

export const guardrailKeys = {
  platform: ['guardrails', 'platform'] as const,
  tenant: (tenantId: string) => ['guardrails', 'tenant', tenantId] as const,
  effective: (tenantId: string, agentId: string) =>
    ['guardrails', 'effective', tenantId, agentId] as const,
};

// =============================================================================
// API Functions
// =============================================================================

async function fetchPlatformGuardrails(): Promise<PlatformGuardrails> {
  const response = await apiClient.get<PlatformGuardrails>(
    '/api/v1/admin/config/guardrails'
  );
  return response.data;
}

async function updatePlatformGuardrails(
  data: PlatformGuardrails
): Promise<PlatformGuardrails> {
  const response = await apiClient.put<PlatformGuardrails>(
    '/api/v1/admin/config/guardrails',
    data
  );
  return response.data;
}

async function fetchTenantGuardrails(
  _tenantId: string
): Promise<string[]> {
  // NOTE: Don't include tenants/{id} in path — apiClient.buildUrl() adds it automatically
  // Path becomes: /api/v1/tenants/{tid}/config/tenant
  const response = await apiClient.get<{ guardrail_prompts?: string[] }>(
    '/api/v1/config/tenant'
  );
  return response.data.guardrail_prompts ?? [];
}

async function updateTenantGuardrails(
  _tenantId: string,
  guardrails: string[]
): Promise<void> {
  // NOTE: Don't include tenants/{id} in path — apiClient.buildUrl() adds it automatically
  // Path becomes: /api/v1/tenants/{tid}/config/tenant
  await apiClient.put('/api/v1/config/tenant', {
    guardrail_prompts: guardrails,
  });
}

async function fetchEffectiveGuardrails(
  _tenantId: string,
  agentId: string
): Promise<EffectiveGuardrails> {
  // NOTE: Don't include tenants/{id} in path — apiClient.buildUrl() adds it automatically
  const response = await apiClient.get<EffectiveGuardrails>(
    `/api/v1/config/agents/${agentId}/effective-guardrails`
  );
  return response.data;
}

// =============================================================================
// Hooks — Platform
// =============================================================================

export function usePlatformGuardrails() {
  return useQuery({
    queryKey: guardrailKeys.platform,
    queryFn: fetchPlatformGuardrails,
    staleTime: 30_000,
  });
}

export function useUpdatePlatformGuardrails() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PlatformGuardrails) => updatePlatformGuardrails(data),
    onSuccess: (data) => {
      queryClient.setQueryData(guardrailKeys.platform, data);
    },
  });
}

// =============================================================================
// Hooks — Tenant
// =============================================================================

export function useTenantGuardrails() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: guardrailKeys.tenant(tenantId ?? ''),
    queryFn: () => fetchTenantGuardrails(tenantId!),
    enabled: !!tenantId,
    staleTime: 30_000,
  });
}

export function useUpdateTenantGuardrails() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (guardrails: string[]) =>
      updateTenantGuardrails(tenantId!, guardrails),
    onSuccess: (_data, guardrails) => {
      queryClient.setQueryData(guardrailKeys.tenant(tenantId!), guardrails);
    },
  });
}

// =============================================================================
// API Functions — Data Governance (DGV)
// =============================================================================

async function fetchPlatformDataGovernance(): Promise<DataGovernanceConfig> {
  const response = await apiClient.get<DataGovernanceConfig>(
    '/api/v1/admin/config/data-governance'
  );
  return response.data;
}

async function updatePlatformDataGovernance(
  data: DataGovernanceConfig
): Promise<DataGovernanceConfig> {
  const response = await apiClient.put<DataGovernanceConfig>(
    '/api/v1/admin/config/data-governance',
    data
  );
  return response.data;
}

async function fetchTenantDataGovernance(
  _tenantId: string
): Promise<DataGovernanceConfig> {
  const response = await apiClient.get<DataGovernanceConfig>(
    '/api/v1/config/data-governance'
  );
  return response.data;
}

async function updateTenantDataGovernance(
  _tenantId: string,
  data: DataGovernanceConfig
): Promise<DataGovernanceConfig> {
  const response = await apiClient.put<DataGovernanceConfig>(
    '/api/v1/config/data-governance',
    data
  );
  return response.data;
}

export function usePlatformDataGovernance() {
  return useQuery({
    queryKey: ['data-governance', 'platform'],
    queryFn: fetchPlatformDataGovernance,
    staleTime: 30_000,
  });
}

export function useUpdatePlatformDataGovernance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: DataGovernanceConfig) => updatePlatformDataGovernance(data),
    onSuccess: (data) => {
      queryClient.setQueryData(['data-governance', 'platform'], data);
    },
  });
}

export function useTenantDataGovernance() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: ['data-governance', 'tenant', tenantId ?? ''],
    queryFn: () => fetchTenantDataGovernance(tenantId!),
    enabled: !!tenantId,
    staleTime: 30_000,
  });
}

export function useUpdateTenantDataGovernance() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: DataGovernanceConfig) =>
      updateTenantDataGovernance(tenantId!, data),
    onSuccess: (data) => {
      queryClient.setQueryData(['data-governance', 'tenant', tenantId!], data);
    },
  });
}

// =============================================================================
// Hooks — Effective (read-only preview for agent detail)
// =============================================================================

export function useEffectiveGuardrails(agentId: string | undefined) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: guardrailKeys.effective(tenantId ?? '', agentId ?? ''),
    queryFn: () => fetchEffectiveGuardrails(tenantId!, agentId!),
    enabled: !!tenantId && !!agentId,
    staleTime: 30_000,
  });
}
